import { Box, Button, Heading, Stack, Text } from '@chakra-ui/react'
import { faMegaphone } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DateTime } from 'luxon'
import React from 'react'

import type { CohostReviewBannerData } from '@/common/services/seriesPage/types'
import chakraTokenToVar from '@/utils/chakraTokenToVar'
import { pluralize } from '@/utils/helpers'

import { useUser } from '@/client/context/user'
import { useServerNow } from '@/client/hooks'

import CohostReviewModal from './CohostReviewModal/CohostReviewModal'

// Only rendered from the series page: SeriesHomeBanners is the sole caller and always
// passes series data, so the session branches this component used to carry were dead
// and are gone here and in the modal, which now always calls the series RPCs.
const CohostReviewBanner: React.FC<{
  series: CohostReviewBannerData
}> = ({ series }) => {
  const currentUser = useUser()
  const now = useServerNow()
  const [showModal, setShowModal] = React.useState(false)

  const pendingCohosts = series.pendingCohosts
  const tutorId = series.tutorUserId
  const numCohostRequests = pendingCohosts.length
  const allowCohosts = series.allowCohosts
  const sessionStarted = DateTime.fromISO(`${series.closedAt}`) < now
  const isSeriesSession = false

  const displayNames = pendingCohosts.map(host => host.displayName)
  const numRequests = displayNames.length

  return (
    <>
      {tutorId === currentUser.id && allowCohosts && !sessionStarted && !isSeriesSession ? (
        <Box bg="yellow.subtle" borderRadius={8} p={4}>
          <Stack direction="row" justify="space-between" alignItems="center">
            <FontAwesomeIcon icon={faMegaphone} color={chakraTokenToVar('fg.warning')} />
            <Box px={4}>
              <Heading size="lg" as="h3">
                {numRequests > 0
                  ? `Review Co-hosts (${numRequests})`
                  : `You're looking for co-hosts!`}
              </Heading>
              {numRequests > 0 ? (
                <Text>
                  {numRequests === 1
                    ? `You received a request from ${displayNames[0]} to become a co-host. Review the request to accept or reject.`
                    : `You received requests from ${displayNames[0]} and ${pluralize(
                        numRequests - 1,
                        'other'
                      )} to become co-hosts. Review the requests to accept or reject.`}
                </Text>
              ) : (
                <Text>
                  You currently have no requests for co-hosting. When you receive a request,
                  we&apos;ll show it here.
                </Text>
              )}
            </Box>
            <Button flexShrink={0} variant="outline" onClick={() => setShowModal(true)} my="5px">
              Review Requests
            </Button>
          </Stack>
        </Box>
      ) : null}
      <CohostReviewModal
        cohosts={pendingCohosts}
        numCohostRequests={numCohostRequests}
        showModal={showModal}
        setShowModal={setShowModal}
      />
    </>
  )
}

export default CohostReviewBanner
